import { BinaryReader, BinaryWriter } from "@bufbuild/protobuf/wire";
import { Ruid } from "../../common/ruid";
import { Value } from "../../common/value";
import { MediaMetadata } from "../../snapdoc/media";
import { SnapSource } from "../media/snap_source";
import { BillboardSignals } from "./billboard_provided_signals";
import { BoltSignals } from "./bolt_provided_signals";
import { CameraSignals } from "./camera_provided_signals";
import { CognacSignals } from "./cognac_provided_signals";
import { ContentManagerSignals } from "./content_manager_provided_signals";
import { CreativeToolsSignals } from "./creative_tools_provided_signals";
import { DiscoverFeedSignals } from "./discover_feed_provided_signals";
import { LensesSignals } from "./lenses_provided_signals";
import { MdpMediaAttribution } from "./mdp_media_attribution";
import { MediaSignals } from "./media_provided_signals";
import { Namespace } from "./namespace";
import { OperaSignals } from "./opera_provided_signals";
import { PerceptionSignals } from "./perception_provided_signals";
import { RecipientsSignals } from "./recipients_provided_signals";
import { RoutingSignals } from "./routing_provided_signals";
import { SnapKitSignals } from "./snap_kit_provided_signals";
import { UploadSignals } from "./upload_provided_signals";
export const protobufPackage = "snapchat.cdp.cof";
export interface FeatureProvidedSignals {
    mediaMetadata: MediaMetadata | undefined;
    storyMetadata: FeatureProvidedSignals_StoryMetadata | undefined;
    cameraSignals: CameraSignals | undefined;
    snapSource: SnapSource;
    contentManagerProvidedSignals: ContentManagerSignals | undefined;
    spectacleMetadata: FeatureProvidedSignals_SpectacleMetadata | undefined;
    boltSignals: BoltSignals | undefined;
    lensesSignals: LensesSignals | undefined;
    creativeToolsSignals: CreativeToolsSignals | undefined;
    estimatedDurationForEventMs: number;
    routingSignals: RoutingSignals | undefined;
    mdpMediaAttribution: MdpMediaAttribution | undefined;
    size: number;
    mediaSignals: MediaSignals | undefined;
    perceptionSignals: PerceptionSignals | undefined;
    operaSignals: OperaSignals | undefined;
    recipientsSignals: RecipientsSignals | undefined;
    billboardSignals: BillboardSignals | undefined;
    cognacSignals: CognacSignals | undefined;
    hashSignals: FeatureProvidedSignals_HashSignals | undefined;
    edgeSignals: {
        [key: number]: Value;
    };
    snapKitSignals: SnapKitSignals | undefined;
    uploadSignals: UploadSignals | undefined;
    ruids: Ruid[];
    discoverFeedSignals: DiscoverFeedSignals | undefined;
}
export enum FeatureProvidedSignals_StoryType {
    UNKNOWN_STORY_TYPE = 0,
    PUBLIC_USER_STORY = 1,
    OUR_STORY = 2,
    PUBLISHER_STORY = 3,
    FRIEND_STORY = 4,
    UNRECOGNIZED = -1
}
export enum FeatureProvidedSignals_SpectaclesVersion {
    UNKNOWN_SPECTACLES_VERSION = 0,
    V1 = 1,
    V2 = 2,
    V3 = 3,
    V4 = 4,
    UNRECOGNIZED = -1
}
export interface FeatureProvidedSignals_StoryMetadata {
    isPublicStory: boolean;
    isOfficialStory: boolean;
    storyType: FeatureProvidedSignals_StoryType;
}
export interface FeatureProvidedSignals_SpectacleMetadata {
    isSpectacle: boolean;
    spectaclesVersion: FeatureProvidedSignals_SpectaclesVersion;
}
export interface FeatureProvidedSignals_HashSignals {
    userId: Uint8Array;
    seed: Uint8Array;
    namespace: Namespace;
}
export interface FeatureProvidedSignals_EdgeSignalsEntry {
    key: number;
    value: Value | undefined;
}
function createBaseFeatureProvidedSignals(): FeatureProvidedSignals {
    return {
        mediaMetadata: undefined,
        storyMetadata: undefined,
        cameraSignals: undefined,
        snapSource: 0,
        contentManagerProvidedSignals: undefined,
        spectacleMetadata: undefined,
        boltSignals: undefined,
        lensesSignals: undefined,
        creativeToolsSignals: undefined,
        estimatedDurationForEventMs: 0,
        routingSignals: undefined,
        mdpMediaAttribution: undefined,
        size: 0,
        mediaSignals: undefined,
        perceptionSignals: undefined,
        operaSignals: undefined,
        recipientsSignals: undefined,
        billboardSignals: undefined,
        cognacSignals: undefined,
        hashSignals: undefined,
        edgeSignals: {},
        snapKitSignals: undefined,
        uploadSignals: undefined,
        ruids: [],
        discoverFeedSignals: undefined,
    };
}
export const FeatureProvidedSignals: MessageFns<FeatureProvidedSignals> = {
    encode(message: FeatureProvidedSignals, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        if (message.mediaMetadata !== undefined) {
            MediaMetadata.encode(message.mediaMetadata, writer.uint32(10).fork()).join();
        }
        if (message.storyMetadata !== undefined) {
            FeatureProvidedSignals_StoryMetadata.encode(message.storyMetadata, writer.uint32(18).fork()).join();
        }
        if (message.cameraSignals !== undefined) {
            CameraSignals.encode(message.cameraSignals, writer.uint32(26).fork()).join();
        }
        if (message.snapSource !== 0) {
            writer.uint32(32).int32(message.snapSource);
        }
        if (message.contentManagerProvidedSignals !== undefined) {
            ContentManagerSignals.encode(message.contentManagerProvidedSignals, writer.uint32(42).fork()).join();
        }
        if (message.spectacleMetadata !== undefined) {
            FeatureProvidedSignals_SpectacleMetadata.encode(message.spectacleMetadata, writer.uint32(50).fork()).join();
        }
        if (message.boltSignals !== undefined) {
            BoltSignals.encode(message.boltSignals, writer.uint32(58).fork()).join();
        }
        if (message.lensesSignals !== undefined) {
            LensesSignals.encode(message.lensesSignals, writer.uint32(66).fork()).join();
        }
        if (message.creativeToolsSignals !== undefined) {
            CreativeToolsSignals.encode(message.creativeToolsSignals, writer.uint32(74).fork()).join();
        }
        if (message.estimatedDurationForEventMs !== 0) {
            writer.uint32(80).uint32(message.estimatedDurationForEventMs);
        }
        if (message.routingSignals !== undefined) {
            RoutingSignals.encode(message.routingSignals, writer.uint32(90).fork()).join();
        }
        if (message.mdpMediaAttribution !== undefined) {
            MdpMediaAttribution.encode(message.mdpMediaAttribution, writer.uint32(98).fork()).join();
        }
        if (message.size !== 0) {
            writer.uint32(104).uint32(message.size);
        }
        if (message.mediaSignals !== undefined) {
            MediaSignals.encode(message.mediaSignals, writer.uint32(114).fork()).join();
        }
        if (message.perceptionSignals !== undefined) {
            PerceptionSignals.encode(message.perceptionSignals, writer.uint32(122).fork()).join();
        }
        if (message.operaSignals !== undefined) {
            OperaSignals.encode(message.operaSignals, writer.uint32(130).fork()).join();
        }
        if (message.recipientsSignals !== undefined) {
            RecipientsSignals.encode(message.recipientsSignals, writer.uint32(138).fork()).join();
        }
        if (message.billboardSignals !== undefined) {
            BillboardSignals.encode(message.billboardSignals, writer.uint32(146).fork()).join();
        }
        if (message.cognacSignals !== undefined) {
            CognacSignals.encode(message.cognacSignals, writer.uint32(154).fork()).join();
        }
        if (message.hashSignals !== undefined) {
            FeatureProvidedSignals_HashSignals.encode(message.hashSignals, writer.uint32(162).fork()).join();
        }
        Object.entries(message.edgeSignals).forEach(([key, value]) => {
            FeatureProvidedSignals_EdgeSignalsEntry.encode({ key: key as any, value }, writer.uint32(170).fork()).join();
        });
        if (message.snapKitSignals !== undefined) {
            SnapKitSignals.encode(message.snapKitSignals, writer.uint32(178).fork()).join();
        }
        if (message.uploadSignals !== undefined) {
            UploadSignals.encode(message.uploadSignals, writer.uint32(186).fork()).join();
        }
        for (const v of message.ruids) {
            Ruid.encode(v!, writer.uint32(194).fork()).join();
        }
        if (message.discoverFeedSignals !== undefined) {
            DiscoverFeedSignals.encode(message.discoverFeedSignals, writer.uint32(202).fork()).join();
        }
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): FeatureProvidedSignals {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseFeatureProvidedSignals();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
                case 1: {
                    if (tag !== 10) {
                        break;
                    }
                    message.mediaMetadata = MediaMetadata.decode(reader, reader.uint32());
                    continue;
                }
                case 2: {
                    if (tag !== 18) {
                        break;
                    }
                    message.storyMetadata = FeatureProvidedSignals_StoryMetadata.decode(reader, reader.uint32());
                    continue;
                }
                case 3: {
                    if (tag !== 26) {
                        break;
                    }
                    message.cameraSignals = CameraSignals.decode(reader, reader.uint32());
                    continue;
                }
                case 4: {
                    if (tag !== 32) {
                        break;
                    }
                    message.snapSource = reader.int32() as any;
                    continue;
                }
                case 5: {
                    if (tag !== 42) {
                        break;
                    }
                    message.contentManagerProvidedSignals = ContentManagerSignals.decode(reader, reader.uint32());
                    continue;
                }
                case 6: {
                    if (tag !== 50) {
                        break;
                    }
                    message.spectacleMetadata = FeatureProvidedSignals_SpectacleMetadata.decode(reader, reader.uint32());
                    continue;
                }
                case 7: {
                    if (tag !== 58) {
                        break;
                    }
                    message.boltSignals = BoltSignals.decode(reader, reader.uint32());
                    continue;
                }
                case 8: {
                    if (tag !== 66) {
                        break;
                    }
                    message.lensesSignals = LensesSignals.decode(reader, reader.uint32());
                    continue;
                }
                case 9: {
                    if (tag !== 74) {
                        break;
                    }
                    message.creativeToolsSignals = CreativeToolsSignals.decode(reader, reader.uint32());
                    continue;
                }
                case 10: {
                    if (tag !== 80) {
                        break;
                    }
                    message.estimatedDurationForEventMs = reader.uint32();
                    continue;
                }
                case 11: {
                    if (tag !== 90) {
                        break;
                    }
                    message.routingSignals = RoutingSignals.decode(reader, reader.uint32());
                    continue;
                }
                case 12: {
                    if (tag !== 98) {
                        break;
                    }
                    message.mdpMediaAttribution = MdpMediaAttribution.decode(reader, reader.uint32());
                    continue;
                }
                case 13: {
                    if (tag !== 104) {
                        break;
                    }
                    message.size = reader.uint32();
                    continue;
                }
                case 14: {
                    if (tag !== 114) {
                        break;
                    }
                    message.mediaSignals = MediaSignals.decode(reader, reader.uint32());
                    continue;
                }
                case 15: {
                    if (tag !== 122) {
                        break;
                    }
                    message.perceptionSignals = PerceptionSignals.decode(reader, reader.uint32());
                    continue;
                }
                case 16: {
                    if (tag !== 130) {
                        break;
                    }
                    message.operaSignals = OperaSignals.decode(reader, reader.uint32());
                    continue;
                }
                case 17: {
                    if (tag !== 138) {
                        break;
                    }
                    message.recipientsSignals = RecipientsSignals.decode(reader, reader.uint32());
                    continue;
                }
                case 18: {
                    if (tag !== 146) {
                        break;
                    }
                    message.billboardSignals = BillboardSignals.decode(reader, reader.uint32());
                    continue;
                }
                case 19: {
                    if (tag !== 154) {
                        break;
                    }
                    message.cognacSignals = CognacSignals.decode(reader, reader.uint32());
                    continue;
                }
                case 20: {
                    if (tag !== 162) {
                        break;
                    }
                    message.hashSignals = FeatureProvidedSignals_HashSignals.decode(reader, reader.uint32());
                    continue;
                }
                case 21: {
                    if (tag !== 170) {
                        break;
                    }
                    const entry21 = FeatureProvidedSignals_EdgeSignalsEntry.decode(reader, reader.uint32());
                    if (entry21.value !== undefined) {
                        message.edgeSignals[entry21.key] = entry21.value;
                    }
                    continue;
                }
                case 22: {
                    if (tag !== 178) {
                        break;
                    }
                    message.snapKitSignals = SnapKitSignals.decode(reader, reader.uint32());
                    continue;
                }
                case 23: {
                    if (tag !== 186) {
                        break;
                    }
                    message.uploadSignals = UploadSignals.decode(reader, reader.uint32());
                    continue;
                }
                case 24: {
                    if (tag !== 194) {
                        break;
                    }
                    message.ruids.push(Ruid.decode(reader, reader.uint32()));
                    continue;
                }
                case 25: {
                    if (tag !== 202) {
                        break;
                    }
                    message.discoverFeedSignals = DiscoverFeedSignals.decode(reader, reader.uint32());
                    continue;
                }
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<FeatureProvidedSignals>, I>>(base?: I): FeatureProvidedSignals {
        return FeatureProvidedSignals.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<FeatureProvidedSignals>, I>>(object: I): FeatureProvidedSignals {
        const message = createBaseFeatureProvidedSignals();
        message.mediaMetadata = (object.mediaMetadata !== undefined && object.mediaMetadata !== null)
            ? MediaMetadata.fromPartial(object.mediaMetadata)
            : undefined;
        message.storyMetadata = (object.storyMetadata !== undefined && object.storyMetadata !== null)
            ? FeatureProvidedSignals_StoryMetadata.fromPartial(object.storyMetadata)
            : undefined;
        message.cameraSignals = (object.cameraSignals !== undefined && object.cameraSignals !== null)
            ? CameraSignals.fromPartial(object.cameraSignals)
            : undefined;
        message.snapSource = object.snapSource ?? 0;
        message.contentManagerProvidedSignals =
            (object.contentManagerProvidedSignals !== undefined && object.contentManagerProvidedSignals !== null)
                ? ContentManagerSignals.fromPartial(object.contentManagerProvidedSignals)
                : undefined;
        message.spectacleMetadata = (object.spectacleMetadata !== undefined && object.spectacleMetadata !== null)
            ? FeatureProvidedSignals_SpectacleMetadata.fromPartial(object.spectacleMetadata)
            : undefined;
        message.boltSignals = (object.boltSignals !== undefined && object.boltSignals !== null)
            ? BoltSignals.fromPartial(object.boltSignals)
            : undefined;
        message.lensesSignals = (object.lensesSignals !== undefined && object.lensesSignals !== null)
            ? LensesSignals.fromPartial(object.lensesSignals)
            : undefined;
        message.creativeToolsSignals = (object.creativeToolsSignals !== undefined && object.creativeToolsSignals !== null)
            ? CreativeToolsSignals.fromPartial(object.creativeToolsSignals)
            : undefined;
        message.estimatedDurationForEventMs = object.estimatedDurationForEventMs ?? 0;
        message.routingSignals = (object.routingSignals !== undefined && object.routingSignals !== null)
            ? RoutingSignals.fromPartial(object.routingSignals)
            : undefined;
        message.mdpMediaAttribution = (object.mdpMediaAttribution !== undefined && object.mdpMediaAttribution !== null)
            ? MdpMediaAttribution.fromPartial(object.mdpMediaAttribution)
            : undefined;
        message.size = object.size ?? 0;
        message.mediaSignals = (object.mediaSignals !== undefined && object.mediaSignals !== null)
            ? MediaSignals.fromPartial(object.mediaSignals)
            : undefined;
        message.perceptionSignals = (object.perceptionSignals !== undefined && object.perceptionSignals !== null)
            ? PerceptionSignals.fromPartial(object.perceptionSignals)
            : undefined;
        message.operaSignals = (object.operaSignals !== undefined && object.operaSignals !== null)
            ? OperaSignals.fromPartial(object.operaSignals)
            : undefined;
        message.recipientsSignals = (object.recipientsSignals !== undefined && object.recipientsSignals !== null)
            ? RecipientsSignals.fromPartial(object.recipientsSignals)
            : undefined;
        message.billboardSignals = (object.billboardSignals !== undefined && object.billboardSignals !== null)
            ? BillboardSignals.fromPartial(object.billboardSignals)
            : undefined;
        message.cognacSignals = (object.cognacSignals !== undefined && object.cognacSignals !== null)
            ? CognacSignals.fromPartial(object.cognacSignals)
            : undefined;
        message.hashSignals = (object.hashSignals !== undefined && object.hashSignals !== null)
            ? FeatureProvidedSignals_HashSignals.fromPartial(object.hashSignals)
            : undefined;
        message.edgeSignals = Object.entries(object.edgeSignals ?? {}).reduce<{
            [key: number]: Value;
        }>((acc, [key, value]) => {
            if (value !== undefined) {
                acc[globalThis.Number(key)] = Value.fromPartial(value);
            }
            return acc;
        }, {});
        message.snapKitSignals = (object.snapKitSignals !== undefined && object.snapKitSignals !== null)
            ? SnapKitSignals.fromPartial(object.snapKitSignals)
            : undefined;
        message.uploadSignals = (object.uploadSignals !== undefined && object.uploadSignals !== null)
            ? UploadSignals.fromPartial(object.uploadSignals)
            : undefined;
        message.ruids = object.ruids?.map((e) => Ruid.fromPartial(e)) || [];
        message.discoverFeedSignals = (object.discoverFeedSignals !== undefined && object.discoverFeedSignals !== null)
            ? DiscoverFeedSignals.fromPartial(object.discoverFeedSignals)
            : undefined;
        return message;
    },
};
function createBaseFeatureProvidedSignals_StoryMetadata(): FeatureProvidedSignals_StoryMetadata {
    return { isPublicStory: false, isOfficialStory: false, storyType: 0 };
}
export const FeatureProvidedSignals_StoryMetadata: MessageFns<FeatureProvidedSignals_StoryMetadata> = {
    encode(message: FeatureProvidedSignals_StoryMetadata, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        if (message.isPublicStory !== false) {
            writer.uint32(8).bool(message.isPublicStory);
        }
        if (message.isOfficialStory !== false) {
            writer.uint32(16).bool(message.isOfficialStory);
        }
        if (message.storyType !== 0) {
            writer.uint32(24).int32(message.storyType);
        }
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): FeatureProvidedSignals_StoryMetadata {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseFeatureProvidedSignals_StoryMetadata();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
                case 1: {
                    if (tag !== 8) {
                        break;
                    }
                    message.isPublicStory = reader.bool();
                    continue;
                }
                case 2: {
                    if (tag !== 16) {
                        break;
                    }
                    message.isOfficialStory = reader.bool();
                    continue;
                }
                case 3: {
                    if (tag !== 24) {
                        break;
                    }
                    message.storyType = reader.int32() as any;
                    continue;
                }
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<FeatureProvidedSignals_StoryMetadata>, I>>(base?: I): FeatureProvidedSignals_StoryMetadata {
        return FeatureProvidedSignals_StoryMetadata.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<FeatureProvidedSignals_StoryMetadata>, I>>(object: I): FeatureProvidedSignals_StoryMetadata {
        const message = createBaseFeatureProvidedSignals_StoryMetadata();
        message.isPublicStory = object.isPublicStory ?? false;
        message.isOfficialStory = object.isOfficialStory ?? false;
        message.storyType = object.storyType ?? 0;
        return message;
    },
};
function createBaseFeatureProvidedSignals_SpectacleMetadata(): FeatureProvidedSignals_SpectacleMetadata {
    return { isSpectacle: false, spectaclesVersion: 0 };
}
export const FeatureProvidedSignals_SpectacleMetadata: MessageFns<FeatureProvidedSignals_SpectacleMetadata> = {
    encode(message: FeatureProvidedSignals_SpectacleMetadata, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        if (message.isSpectacle !== false) {
            writer.uint32(8).bool(message.isSpectacle);
        }
        if (message.spectaclesVersion !== 0) {
            writer.uint32(16).int32(message.spectaclesVersion);
        }
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): FeatureProvidedSignals_SpectacleMetadata {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseFeatureProvidedSignals_SpectacleMetadata();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
                case 1: {
                    if (tag !== 8) {
                        break;
                    }
                    message.isSpectacle = reader.bool();
                    continue;
                }
                case 2: {
                    if (tag !== 16) {
                        break;
                    }
                    message.spectaclesVersion = reader.int32() as any;
                    continue;
                }
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<FeatureProvidedSignals_SpectacleMetadata>, I>>(base?: I): FeatureProvidedSignals_SpectacleMetadata {
        return FeatureProvidedSignals_SpectacleMetadata.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<FeatureProvidedSignals_SpectacleMetadata>, I>>(object: I): FeatureProvidedSignals_SpectacleMetadata {
        const message = createBaseFeatureProvidedSignals_SpectacleMetadata();
        message.isSpectacle = object.isSpectacle ?? false;
        message.spectaclesVersion = object.spectaclesVersion ?? 0;
        return message;
    },
};
function createBaseFeatureProvidedSignals_HashSignals(): FeatureProvidedSignals_HashSignals {
    return { userId: new Uint8Array(0), seed: new Uint8Array(0), namespace: 0 };
}
export const FeatureProvidedSignals_HashSignals: MessageFns<FeatureProvidedSignals_HashSignals> = {
    encode(message: FeatureProvidedSignals_HashSignals, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        if (message.userId.length !== 0) {
            writer.uint32(10).bytes(message.userId);
        }
        if (message.seed.length !== 0) {
            writer.uint32(18).bytes(message.seed);
        }
        if (message.namespace !== 0) {
            writer.uint32(24).int32(message.namespace);
        }
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): FeatureProvidedSignals_HashSignals {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseFeatureProvidedSignals_HashSignals();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
                case 1: {
                    if (tag !== 10) {
                        break;
                    }
                    message.userId = reader.bytes();
                    continue;
                }
                case 2: {
                    if (tag !== 18) {
                        break;
                    }
                    message.seed = reader.bytes();
                    continue;
                }
                case 3: {
                    if (tag !== 24) {
                        break;
                    }
                    message.namespace = reader.int32() as any;
                    continue;
                }
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<FeatureProvidedSignals_HashSignals>, I>>(base?: I): FeatureProvidedSignals_HashSignals {
        return FeatureProvidedSignals_HashSignals.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<FeatureProvidedSignals_HashSignals>, I>>(object: I): FeatureProvidedSignals_HashSignals {
        const message = createBaseFeatureProvidedSignals_HashSignals();
        message.userId = object.userId ?? new Uint8Array(0);
        message.seed = object.seed ?? new Uint8Array(0);
        message.namespace = object.namespace ?? 0;
        return message;
    },
};
function createBaseFeatureProvidedSignals_EdgeSignalsEntry(): FeatureProvidedSignals_EdgeSignalsEntry {
    return { key: 0, value: undefined };
}
export const FeatureProvidedSignals_EdgeSignalsEntry: MessageFns<FeatureProvidedSignals_EdgeSignalsEntry> = {
    encode(message: FeatureProvidedSignals_EdgeSignalsEntry, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        if (message.key !== 0) {
            writer.uint32(8).int32(message.key);
        }
        if (message.value !== undefined) {
            Value.encode(message.value, writer.uint32(18).fork()).join();
        }
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): FeatureProvidedSignals_EdgeSignalsEntry {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseFeatureProvidedSignals_EdgeSignalsEntry();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
                case 1: {
                    if (tag !== 8) {
                        break;
                    }
                    message.key = reader.int32();
                    continue;
                }
                case 2: {
                    if (tag !== 18) {
                        break;
                    }
                    message.value = Value.decode(reader, reader.uint32());
                    continue;
                }
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<FeatureProvidedSignals_EdgeSignalsEntry>, I>>(base?: I): FeatureProvidedSignals_EdgeSignalsEntry {
        return FeatureProvidedSignals_EdgeSignalsEntry.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<FeatureProvidedSignals_EdgeSignalsEntry>, I>>(object: I): FeatureProvidedSignals_EdgeSignalsEntry {
        const message = createBaseFeatureProvidedSignals_EdgeSignalsEntry();
        message.key = object.key ?? 0;
        message.value = (object.value !== undefined && object.value !== null) ? Value.fromPartial(object.value) : undefined;
        return message;
    },
};
type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;
export type DeepPartial<T> = T extends Builtin ? T : T extends globalThis.Array<infer U> ? globalThis.Array<DeepPartial<U>> : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>> : T extends {} ? {
    [K in keyof T]?: DeepPartial<T[K]>;
} : Partial<T>;
type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P : P & {
    [K in keyof P]: Exact<P[K], I[K]>;
} & {
    [K in Exclude<keyof I, KeysOfUnion<P>>]: never;
};
export interface MessageFns<T> {
    encode(message: T, writer?: BinaryWriter): BinaryWriter;
    decode(input: BinaryReader | Uint8Array, length?: number): T;
    create<I extends Exact<DeepPartial<T>, I>>(base?: I): T;
    fromPartial<I extends Exact<DeepPartial<T>, I>>(object: I): T;
}
